import { DefaultFocusStatusModel } from "../../../models/DefaultFocusStatusModel";
import { QuestionType } from "../../../models/enums/QuestionType";
import { setDefaultFocusStatus } from "../../../redux/actions";

export abstract class KeyboardNavigation {
    protected defaultFocusStatus;
    protected dispatch;
    protected question;
    protected isHybridGrid;
    protected isRunningTotal;

    constructor(defaultFocusStatus, dispatch, question) {
        this.defaultFocusStatus = defaultFocusStatus;
        this.dispatch = dispatch;
        this.question = question;
        this.isHybridGrid =
            this.question &&
            this.question.parent &&
            this.question.parent.type === QuestionType.HybridGrid;
        this.isRunningTotal =
            this.question && this.question.parent && this.question.parent.type === QuestionType.RunningTotal;
    }

    abstract findNextFocusableElementWithinSameVariable(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null;

    abstract findNextFocusableElementWithinNextVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null;

    abstract findLastFocusableElementWithinPreviousVariable(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null;

    findNextFocusableElementWithinNextVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        if (this.isHybridGrid) {
            return this.findNextFocusableElementWithinNextVariableCommonForHybridGrid(
                currentElement,
            );
        }

        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];

            if (elementFocusStatus.elementId.includes("IMAGE_CHOICE_GRID")) {
                let elementRef = document.getElementById(elementFocusStatus.elementId)
                if (elementRef) {
                    elementFocusStatus.elementRef.current = elementRef;
                }
            }

            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;
            if (elementFocusStatus.elementRef?.current &&
                ((elementFocusStatus.variableId !== 0 && variableCondition) ||
                    (elementFocusStatus.variableId === currentElement.variableId &&
                        elementFocusStatus.elementRef?.current?.tagName === "TEXTAREA"))
            ) {
                return this.getFocusableElement(elementFocusStatus, true);
            }
        }
        return null;
    }

    findNextFocusableElementWithinNextVariableCommonForHybridGrid(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        for (let i = index + 1, len = this.defaultFocusStatus.statuses.length; i < len; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];

            if (
                elementFocusStatus.questionId !== 0 &&
                elementFocusStatus.questionId !== currentElement.questionId &&
                elementFocusStatus.elementRef?.current
            ) {
                return this.getFocusableElement(elementFocusStatus, true);;
            }
        }
        return null;
    }

    findLastFocusableElementWithinPreviousVariableCommon(
        currentElement: DefaultFocusStatusModel,
        variableIds: number[] = null,
    ): DefaultFocusStatusModel | null {
        if (this.isHybridGrid) {
            return this.findLastFocusableElementWithinPreviousVariableCommonForHybridGrid(
                currentElement,
            );
        }

        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus: DefaultFocusStatusModel = this.defaultFocusStatus.statuses[i];

            if (elementFocusStatus.elementId.includes("IMAGE_CHOICE_GRID")) {
                let elementRef = document.getElementById(elementFocusStatus.elementId)
                if (elementRef) {
                    elementFocusStatus.elementRef.current = elementRef;
                }
            }

            const variableCondition =
                variableIds && variableIds.length > 0
                    ? !variableIds.includes(elementFocusStatus.variableId)
                    : elementFocusStatus.variableId !== currentElement.variableId;

            if (elementFocusStatus.variableId !== 0 && variableCondition &&
                elementFocusStatus.elementRef?.current) {
                return this.getFocusableElement(elementFocusStatus);
            }
        }
        return null;
    }

    findLastFocusableElementWithinPreviousVariableCommonForHybridGrid(
        currentElement: DefaultFocusStatusModel,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        for (let i = index - 1; i >= 0; i--) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            if (
                elementFocusStatus.questionId !== 0 &&
                elementFocusStatus.questionId !== currentElement.questionId &&
                elementFocusStatus.elementRef?.current
            ) {
                return this.getFocusableElement(elementFocusStatus);
            }
        }

        return null;
    }

    findNextFocusableElementWithinSameVariableCommonForHybridGrid(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        if (eventKey === "ArrowRight") {
            for (let i = index; i < this.defaultFocusStatus.statuses.length; i++) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];

                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.rowIndex === currentElement.rowIndex &&
                    elementFocusStatus.elementId !== currentElement.elementId
                ) {
                    return this.getFocusableElement(elementFocusStatus);
                }
            }
        } else {
            for (let i = index; i >= 0; i--) {
                const elementFocusStatus = this.defaultFocusStatus.statuses[i];
                if (
                    elementFocusStatus.questionId === currentElement.questionId &&
                    elementFocusStatus.rowIndex === currentElement.rowIndex &&
                    elementFocusStatus.elementId !== currentElement.elementId &&
                    elementFocusStatus.elementRef?.current
                ) {
                    return this.getFocusableElement(elementFocusStatus);
                }
            }
        }

        return null;
    }

    findNextFocusableElementWithinSameVariableCommonForSwitchingRowsForHybridGrid(
        currentElement: DefaultFocusStatusModel,
        eventKey: string,
    ): DefaultFocusStatusModel | null {
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);
        const currentQuestionRowIndexes: number[] =
            this.defaultFocusStatus.statuses
                ?.filter((status) => status.questionId === currentElement.questionId)
                .map((s) => s.rowIndex) || [];
        const currentQuestionUniqueRowIndexes = currentQuestionRowIndexes.filter(
            (item, currIndex) => currentQuestionRowIndexes.indexOf(item) === currIndex,
        );

        const currentRowIndex = currentQuestionUniqueRowIndexes.indexOf(currentElement.rowIndex);
        let requiredRowIndex = currentElement.rowIndex;
        const isQuestionLastRowIndex =
            currentRowIndex === currentQuestionUniqueRowIndexes.length - 1;
        const isQuestionFirstRowIndex = currentRowIndex === 0;

        if (eventKey === "ArrowDown" && isQuestionLastRowIndex) {
            return null;
        }
        if (eventKey === "ArrowUp" && isQuestionFirstRowIndex) {
            return null;
        }

        if (
            eventKey === "ArrowDown" &&
            currentRowIndex !== -1 &&
            currentRowIndex < currentQuestionUniqueRowIndexes.length - 1
        ) {
            requiredRowIndex = currentQuestionUniqueRowIndexes[currentRowIndex + 1];
        } else if (eventKey === "ArrowUp" && currentRowIndex > 0) {
            requiredRowIndex = currentQuestionUniqueRowIndexes[currentRowIndex - 1];
        }

        for (let i = 0; i < this.defaultFocusStatus.statuses.length; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            const indexCondition = eventKey === "ArrowDown" ? i > index : i < index;

            if (
                indexCondition &&
                elementFocusStatus.questionId === currentElement.questionId &&
                elementFocusStatus.rowIndex === requiredRowIndex &&
                elementFocusStatus.elementId !== currentElement.elementId
            ) {
                return elementFocusStatus;
            }
        }

        return null;
    }

    updateNewFocusStatus(elementWithFocus: DefaultFocusStatusModel) {
        this.defaultFocusStatus.statuses.forEach((element) => {
            element.focusStatus = false;
        });

        const index = this.defaultFocusStatus.statuses.indexOf(elementWithFocus);
        if (index !== -1) {
            this.defaultFocusStatus.statuses[index].focusStatus = true;
            this.dispatch(setDefaultFocusStatus(this.defaultFocusStatus));
        }
    }

    removeFocusForRemainingElementsOfLastQuestion(currentElement: DefaultFocusStatusModel) {
        const elementsToUpdate: DefaultFocusStatusModel[] = [];
        const index = this.defaultFocusStatus.statuses.indexOf(currentElement);

        for (let i = index + 1; i < this.defaultFocusStatus.statuses.length; i++) {
            const elementFocusStatus = this.defaultFocusStatus.statuses[i];
            if (
                elementFocusStatus.variableId !== 0 &&
                elementFocusStatus.elementId !== currentElement.elementId
            ) {
                elementsToUpdate.push(elementFocusStatus);
            }
        }

        elementsToUpdate.forEach((element) => {
            if (element.elementRef && element.elementRef.current) {
                element.elementRef.current.setAttribute("tabindex", "-1");
            }
        });
    }

    scroll(elementToFocus) {
        const rect = elementToFocus.getBoundingClientRect();
        const isInViewport =
            rect.top >= 0 &&
            rect.left >= 0 &&
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);

        if (!isInViewport) {
            elementToFocus.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }

    handleKeyPressEvent(
        event: React.KeyboardEvent<HTMLDivElement>,
        currentElementInfo: DefaultFocusStatusModel,
    ) {
        if (!currentElementInfo) return;

        let nextFocusableElementInfo;

        switch (event.key) {
            case "ArrowLeft":
            case "ArrowRight":
            case "ArrowUp":
            case "ArrowDown":
                nextFocusableElementInfo = this.findNextFocusableElementWithinSameVariable(
                    currentElementInfo,
                    event.key,
                );
                break;
            case "Tab":
                if (event.shiftKey) {
                    nextFocusableElementInfo = this.findLastFocusableElementWithinPreviousVariable(
                        currentElementInfo,
                    );
                } else {
                    nextFocusableElementInfo = this.findNextFocusableElementWithinNextVariable(
                        currentElementInfo,
                    );
                }
                break;
            default:
                return;
        }

        if (nextFocusableElementInfo && nextFocusableElementInfo.elementRef.current) {
            const elementToFocus = nextFocusableElementInfo.elementRef.current;
            if (elementToFocus && typeof elementToFocus.scrollIntoView === 'function') {
                this.scroll(elementToFocus);
            }
            elementToFocus.focus();
            event.preventDefault();
            this.updateNewFocusStatus(nextFocusableElementInfo);
        } else {
            this.removeFocusForRemainingElementsOfLastQuestion(currentElementInfo);
        }
    }

    getFocusableElement(elementFocusStatus: DefaultFocusStatusModel, isNextElement: boolean = false) {
        if (elementFocusStatus.lookupTableGroupType === 'lookupTableRow') {
            return this.findFirstFocusableElementTableBody(elementFocusStatus);
        }
        return elementFocusStatus;
    }

    findFirstFocusableElementTableBody(elementFocusStatus: DefaultFocusStatusModel) {
        const parentElement = elementFocusStatus.elementRef.current.closest('tbody');
        const firstElement = parentElement.querySelector(':first-child');
        let index = this.defaultFocusStatus.statuses.findIndex((status) => {
            return status.elementId === firstElement.id;
        })
        if (index === -1) {
            return elementFocusStatus;
        }
        return this.defaultFocusStatus.statuses[index];
    }

    findNextElementInLookupTable(currentElementInfo: DefaultFocusStatusModel) {
        const findTableRowElement = this.defaultFocusStatus.statuses.find((element: DefaultFocusStatusModel) => {
            return element.elementRef?.current &&
                element.lookupTableGroupType === 'lookupTableRow' &&
                element.questionId === currentElementInfo.questionId &&
                element.elementRef?.current?.closest('tbody');
        });
        if (findTableRowElement) {
            return this.findFirstFocusableElementTableBody(findTableRowElement);
        }
    }
}
